if (!customElements.get('lcn-navigation')) {
  class LcnNavigation extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.dialog = this.querySelector('dialog');
      this.openers = [...this.querySelectorAll('[data-open-menu]')];
      this.desktop = this.querySelector('.lcn-desktop-list');
      this.home = this.dialog?.querySelector('[data-mobile-home]');
      this.scroll = this.dialog?.querySelector('.lcn-mobile-scroll');
      this.pages = [...this.dialog?.querySelectorAll('[data-mobile-page]') || []];

      if (!this.dialog || !this.desktop || !this.home || !this.scroll) {
        this.events.abort();
        this.events = null;
        return;
      }

      this.setupServices();

      for (const opener of this.openers) {
        opener.addEventListener('click', () => this.openDrawer(opener.dataset.openMenu), { signal });
      }

      this.dialog.querySelector('[data-close-menu]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('close', () => this.afterClose(), { signal });

      this.dialog.addEventListener('click', event => {
        if (event.target.closest('a[href]')) {
          this.dialog.close();
          return;
        }
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (
          event.clientX < box.left ||
          event.clientX > box.right ||
          event.clientY < box.top ||
          event.clientY > box.bottom
        ) this.dialog.close();
      }, { signal });

      for (const button of this.dialog.querySelectorAll('[data-mobile-open]')) {
        button.addEventListener('click', () => this.showMobile(button.dataset.mobileOpen), { signal });
      }

      for (const button of this.dialog.querySelectorAll('[data-mobile-back]')) {
        button.addEventListener('click', () => this.showMobile('shop'), { signal });
      }

      this.breakpoint = matchMedia('(min-width: 900px)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');

      for (const disclosure of this.desktop.querySelectorAll('[data-top-disclosure]')) {
        disclosure.addEventListener('toggle', () => {
          if (!disclosure.open) return;
          for (const sibling of this.desktop.querySelectorAll('[data-top-disclosure]')) {
            if (sibling !== disclosure) sibling.open = false;
          }
          this.fitDesktop(disclosure);
        }, { signal });

        disclosure.addEventListener('pointerenter', event => {
          clearTimeout(this.hoverTimer);
          if (
            this.dataset.openTrigger === 'click' ||
            this.editorCategory ||
            !this.breakpoint.matches ||
            !this.finePointer.matches ||
            event.pointerType === 'touch'
          ) return;

          this.hoverTimer = setTimeout(() => {
            const focused = this.desktop.querySelector('[data-top-disclosure][open]');
            if (
              focused &&
              focused !== disclosure &&
              focused.contains(document.activeElement) &&
              document.activeElement.matches(':focus-visible')
            ) return;
            this.closeDesktop();
            disclosure.open = true;
          }, 140);
        }, { signal });

        disclosure.addEventListener('pointerleave', () => {
          clearTimeout(this.hoverTimer);
          if (
            this.dataset.openTrigger === 'click' ||
            this.editorCategory === disclosure.dataset.category
          ) return;
          this.hoverTimer = setTimeout(() => {
            if (
              !disclosure.contains(document.activeElement) ||
              !document.activeElement.matches(':focus-visible')
            ) disclosure.open = false;
          }, 260);
        }, { signal });
      }

      for (const button of this.desktop.querySelectorAll('[data-close-desktop]')) {
        button.addEventListener('click', () => {
          const disclosure = button.closest('[data-top-disclosure]');
          disclosure.open = false;
          disclosure.querySelector('summary').focus();
        }, { signal });
      }

      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.dialog.open) return;
        const open = this.desktop.querySelector('[data-top-disclosure][open]');
        if (open) {
          this.closeDesktop();
          open.querySelector('summary').focus();
          event.preventDefault();
        }
      }, { signal });

      document.addEventListener('click', event => {
        if (!this.editorCategory && !this.contains(event.target)) this.closeDesktop();
      }, { signal });

      this.desktop.addEventListener('focusout', event => {
        if (
          !this.editorCategory &&
          event.relatedTarget &&
          !this.desktop.contains(event.relatedTarget)
        ) this.closeDesktop();
      }, { signal });

      for (const directory of this.querySelectorAll('[data-brand-directory]')) {
        this.setupBrands(directory, signal);
      }

      for (const form of this.dialog.querySelectorAll('form.lcn-localization')) {
        form.addEventListener('change', event => {
          if (!event.target.matches('select')) return;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(form.querySelector('[data-localization-submit]'));
          } else {
            form.submit();
          }
        }, { signal });
        form.classList.add('lcn-localization-ready');
      }

      this.breakpoint.addEventListener('change', () => {
        if (this.breakpoint.matches && this.dialog.open) this.dialog.close();
        this.closeDesktop();
      }, { signal });

      window.addEventListener('resize', () => {
        const open = this.desktop.querySelector('[data-top-disclosure][open]');
        if (open) this.fitDesktop(open);
      }, { signal });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', event => {
          const block = [...this.querySelectorAll('[data-lcn-image-block]')].find(
            item => item.dataset.lcnImageBlock === String(event.detail?.blockId)
          );
          if (!block) return;
          this.editorCategory = block.dataset.lcnImageCategory;

          if (this.breakpoint.matches) {
            this.closeDesktop();
            const menu = [...this.desktop.querySelectorAll('[data-top-disclosure]')].find(
              item => item.dataset.category === this.editorCategory
            );
            if (menu) menu.open = true;
          } else {
            this.openDrawer(this.editorCategory);
          }
        }, { signal });

        document.addEventListener('shopify:block:deselect', event => {
          const block = [...this.querySelectorAll('[data-lcn-image-block]')].find(
            item => item.dataset.lcnImageBlock === String(event.detail?.blockId)
          );
          if (!block || block.dataset.lcnImageCategory !== this.editorCategory) return;
          this.editorCategory = null;
          this.closeDesktop();
          if (this.dialog.open) this.dialog.close();
        }, { signal });
      }

      this.showMobile('shop', false);
      this.dataset.ready = 'true';
    }

    fitDesktop(disclosure) {
      const panel = disclosure.querySelector('.lcn-panel');
      if (panel) {
        panel.style.setProperty(
          '--lcn-panel-offset',
          Math.max(0, panel.getBoundingClientRect().top) + 'px'
        );
      }
    }

    setupServices() {
      const container = this.desktop.querySelector('.lcn-menu-links--services');
      const source = container?.querySelector(':scope > .lcn-columns');
      if (!source || container.querySelector('.lcn-compact-services')) return;

      const groups = [...source.querySelectorAll('.lcn-column:not(.lcn-editorial)')];
      if (!groups.length) return;

      const fr = document.documentElement.lang.toLowerCase().startsWith('fr');
      const labels = fr ? {
        facials: 'Soins Loshen',
        signature: 'Soins signature',
        advanced: 'Soins avancés',
        body: 'Soins du corps',
        consult: 'Consultations'
      } : {
        facials: 'Loshen facials',
        signature: 'Signature facials',
        advanced: 'Advanced skin care',
        body: 'Body care',
        consult: 'Consultations'
      };

      const shorter = {
        'la signature loshen facial': 'La Signature Loshen',
        'the advanced facial': 'The Advanced',
        'the fundamental facial': 'The Fundamental',
        "l'homme - for men": 'L’HOMME',
        'the valmont signature facial': 'Valmont',
        'the biologique recherche methodology': 'Biologique Recherche',
        'evidens de beaute': 'EviDens de Beauté',
        'the facial masterpiece by elixir des glaciers': 'Elixir des Glaciers',
        'the fire and ice by is clinical': 'iS Clinical Fire & Ice',
        'the lifted, enhanced, and sculpted facial': 'Lifted, Enhanced & Sculpted',
        'the skin starter teen facial': 'Skin Starter Teen',
        '[comfort zone] body': 'Comfort Zone Body',
        'lpg endermologie®': 'LPG Endermologie',
        'bloomea® - skin resurfacing': 'Bloomea',
        'neoskin by aerolase™': 'Aerolase NeoSkin',
        'the chemical peel': 'Chemical Peel',
        'the exilis ultra - skin tightening': 'Exilis Ultra',
        'the microneedling - collagen induction': 'Collagen Induction',
        'the consultation': 'Skin Consultations'
      };

      const clean = value => value.trim().replace(/\s+/g, ' ');
      let copyNumber = 0;

      const copy = item => {
        const clone = item.cloneNode(true);
        const nodes = [clone, ...clone.querySelectorAll('*')];
        const ids = new Map();
        const suffix = '-lcn-compact-' + (++copyNumber);

        for (const node of nodes) {
          if (!node.id) continue;
          ids.set(node.id, node.id + suffix);
          node.id += suffix;
        }

        for (const node of nodes) {
          for (const name of ['aria-controls', 'aria-labelledby', 'aria-describedby', 'for']) {
            if (!node.hasAttribute(name)) continue;
            node.setAttribute(
              name,
              node.getAttribute(name).split(/\s+/).map(id => ids.get(id) || id).join(' ')
            );
          }
        }

        const links = [...clone.querySelectorAll('a[href]')];
        if (clone.matches('a[href]')) links.unshift(clone);

        for (const link of links) {
          const full = clean(link.textContent);
          const short = shorter[this.normalise(full)];
          if (!short || link.querySelector('img, svg')) continue;
          link.textContent = short;
          link.setAttribute('aria-label', full);
          if (!link.hasAttribute('title')) link.setAttribute('title', full);
        }

        return clone;
      };

      const buckets = {
        facials: [],
        signature: [],
        advanced: [],
        body: [],
        consult: []
      };
      const extras = [];

      for (const group of groups) {
        const heading = group.querySelector(':scope > .lcn-column-title');
        const list = group.querySelector(':scope > .lcn-links');
        const name = this.normalise(heading?.textContent || '');

        if (!list || !list.children.length) {
          extras.push(copy(group));
          continue;
        }

        let type = '';
        if (/consult|event|evenement/.test(name)) type = 'consult';
        else if (/facial|soins?.*(visage|signature|loshen)/.test(name)) type = 'facials';
        else if (/technolog|body|corps|advanced|avance/.test(name)) type = 'advanced';

        if (!type) {
          extras.push(copy(group));
          continue;
        }

        for (const item of list.children) {
          const text = this.normalise(item.textContent);
          const href = item.querySelector('a[href]')?.getAttribute('href') || '';
          let bucket = type;

          if (
            type === 'facials' &&
            /valmont|biologique|br-methodology|evidens|elixir|facial-masterpiece|is clinical|fire.and.ice/.test(text + ' ' + href)
          ) bucket = 'signature';

          if (
            type === 'advanced' &&
            /comfort.zone|endermolog|\blpg\b|\bbody\b|\bcorps\b/.test(text + ' ' + href)
          ) bucket = 'body';

          buckets[bucket].push(copy(item));
        }
      }

      const grid = document.createElement('div');
      grid.className = 'lcn-compact-services';

      const section = (label, items) => {
        if (!items.length) return null;
        const group = document.createElement('section');
        group.className = 'lcn-column';
        const heading = document.createElement('h3');
        heading.className = 'lcn-column-title';
        heading.textContent = label;
        const list = document.createElement('ul');
        list.className = 'lcn-links';
        list.setAttribute('role', 'list');
        list.append(...items);
        group.append(heading, list);
        return group;
      };

      for (const key of ['facials', 'signature', 'advanced']) {
        const group = section(labels[key], buckets[key]);
        if (group) grid.append(group);
      }

      const stack = document.createElement('div');
      stack.className = 'lcn-column-stack';

      const body = section(labels.body, buckets.body);
      if (body) stack.append(body);

      const consult = section(labels.consult, buckets.consult);
      if (consult) stack.append(consult);

      stack.append(...extras);
      grid.append(stack);

      const urlKey = link => {
        try {
          return new URL(link.getAttribute('href'), document.baseURI).href;
        } catch (_) {
          return link.getAttribute('href');
        }
      };

      const represented = new Set([...grid.querySelectorAll('a[href]')].map(urlKey));
      const remaining = [];
      const original = [
        ...source.querySelectorAll('.lcn-editorial a[href]'),
        ...source.querySelectorAll('a[href]')
      ];

      for (const link of original) {
        if (
          !link.getAttribute('href') ||
          link.getAttribute('href') === '#' ||
          represented.has(urlKey(link))
        ) continue;

        const item = document.createElement('li');
        const clone = copy(link);
        clone.classList.remove('lcn-book', 'lcn-text-link');
        item.append(clone);
        remaining.push(item);
        represented.add(urlKey(link));
      }

      if (remaining.length) {
        const list = consult?.querySelector('.lcn-links');
        if (list) list.append(...remaining);
        else stack.append(section(labels.consult, remaining));
      }

      container.append(grid);
      source.hidden = true;
    }

    setupBrands(directory, signal) {
      const input = directory.querySelector('[data-brand-filter]');
      const list = directory.querySelector('[data-brand-list]');
      const buttons = [...directory.querySelectorAll('[data-brand-letter]')];
      const empty = directory.querySelector('[data-brand-empty]');
      const status = directory.querySelector('[data-brand-count]');
      if (!input || !list || !empty || !status) return;

      const seen = new Set();
      const brands = [...list.querySelectorAll('[data-brand-name]')].filter(item => {
        const link = item.querySelector('.lcn-brand-entry > a');
        const key = link?.getAttribute('href');
        if (key && !item.querySelector('.lcn-brand-extra')) {
          if (seen.has(key)) {
            item.remove();
            return false;
          }
          seen.add(key);
        }
        return true;
      });

      const collator = new Intl.Collator(document.documentElement.lang || 'en', {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true
      });

      brands.sort((a, b) => collator.compare(a.dataset.brandName, b.dataset.brandName));
      for (const item of brands) list.append(item);

      const initials = new Set(brands.map(item => this.brandInitial(item.dataset.brandName)));

      for (const button of buttons) {
        button.disabled =
          button.dataset.brandLetter !== 'all' &&
          !initials.has(button.dataset.brandLetter);
      }

      let selected = 'all';

      const filter = () => {
        const query = this.normalise(input.value.trim());
        let count = 0;

        for (const item of brands) {
          const show =
            (selected === 'all' || this.brandInitial(item.dataset.brandName) === selected) &&
            this.normalise(item.dataset.brandName).includes(query);
          item.hidden = !show;
          if (show) count++;
        }

        for (const button of buttons) {
          button.setAttribute('aria-pressed', String(button.dataset.brandLetter === selected));
        }

        empty.hidden = count !== 0;
        const countLabel = count === 1 ? directory.dataset.brandCountOne : directory.dataset.brandCountLabel;
        status.textContent =
          count + ' ' + (countLabel || 'brands') +
          (selected === 'all' ? '' : ' · ' + selected);
      };

      for (const button of buttons) {
        button.addEventListener('click', () => {
          selected = button.dataset.brandLetter;
          input.value = '';
          filter();
        }, { signal });
      }

      input.addEventListener('input', () => {
        selected = 'all';
        filter();
      }, { signal });

      directory.classList.add('lcn-filter-ready');
      filter();
    }

    normalise(value) {
      return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    }

    brandInitial(value) {
      const first = this.normalise(value).replace(/^[^a-z0-9]+/, '').charAt(0).toUpperCase();
      return /^[A-Z]$/.test(first) ? first : '#';
    }

    closeDesktop() {
      clearTimeout(this.hoverTimer);
      for (const disclosure of this.desktop.querySelectorAll('[data-top-disclosure][open]')) {
        disclosure.open = false;
      }
    }

    showMobile(key, focus = true) {
      const page = this.pages.find(item => item.dataset.mobilePage === key);
      const selected = page ? key : 'shop';

      this.dialog.dataset.mobilePageActive = selected;
      this.dialog.dataset.showBooking = String(selected === 'shop' || selected === 'services');

      const book = this.dialog.querySelector('.lcn-drawer-footer .lcn-book');
      if (book) book.hidden = this.dialog.dataset.showBooking !== 'true';

      this.home.hidden = selected !== 'shop';
      for (const item of this.pages) item.hidden = item !== page;

      for (const button of this.dialog.querySelectorAll('[data-mobile-open]')) {
        button.setAttribute('aria-expanded', String(button.dataset.mobileOpen === selected));
      }

      this.scroll.scrollTop = 0;

      if (focus) {
        const target = selected === 'shop'
          ? this.home.querySelector('[data-mobile-open]')
          : page.querySelector('h2');
        target?.focus({ preventScroll: true });
      }
    }

    openDrawer(key = 'shop') {
      if (this.dialog.open) {
        this.showMobile(key);
        return;
      }

      this.closeDesktop();
      this.returnFocus = document.activeElement;
      this.oldOverflow = document.body.style.overflow;
      this.showMobile(key, false);
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';

      for (const opener of this.openers) {
        opener.setAttribute('aria-expanded', String(opener.dataset.openMenu === key));
      }

      if (key !== 'shop') this.showMobile(key);
    }

    afterClose() {
      if (this.oldOverflow !== undefined) {
        document.body.style.overflow = this.oldOverflow;
        this.oldOverflow = undefined;
      }

      for (const opener of this.openers) opener.setAttribute('aria-expanded', 'false');

      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);

      if (this.dialog?.open) {
        this.dialog.close();
        this.afterClose();
      }

      this.events?.abort();
      this.events = null;
      this.editorCategory = null;
    }
  }

  customElements.define('lcn-navigation', LcnNavigation);
}

(() => {
  const styleId = 'lcn-mobile-detail-style';

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .header.lcn-header {
        --lcn-paper: #fff;
        --lcn-highlight: #fff;
        --background: 255 255 255;
      }

      .lcn-drawer {
        --lcn-paper: #fff;
        --lcn-highlight: #fff;
      }

      .lcn-header .lcn-product-search input:focus-visible {
        outline: none;
      }

      .lcn-header .lcn-product-search:focus-within {
        outline: 1px solid currentColor;
        outline-offset: 3px;
        border-radius: 999px;
      }

      .lcn-image-card {
        position: relative;
        overflow: hidden;
        border-radius: 4px;
      }

      .lcn-image-card .lcn-image-caption {
        position: absolute;
        inset: auto 0 0;
        padding: 42px 14px 14px;
        background: linear-gradient(transparent, rgba(0,0,0,.78));
        color: #fff;
      }

      .lcn-image-card .lcn-image-heading {
        font-size: 18px;
        line-height: 1.3;
      }

      .lcn-image-card .lcn-image-text {
        font-size: 12px;
        line-height: 1.5;
      }

      @media (min-width: 900px) {
        lcn-navigation .lcn-desktop-list .lcn-panel {
          max-height: min(68vh, calc(100dvh - var(--lcn-panel-offset, 180px) - 16px));
          overflow-y: auto;
        }

        lcn-navigation .lcn-desktop-list .lcn-panel-inner {
          position: relative;
          padding: 28px 0;
        }

        lcn-navigation .lcn-desktop-list .lcn-panel-heading {
          position: absolute;
          top: 0;
          right: -8px;
          margin: 0;
          z-index: 2;
        }

        lcn-navigation .lcn-desktop-list .lcn-panel-heading h2 {
          display: none;
        }

        lcn-navigation .lcn-desktop-list .lcn-panel-heading .lcn-close {
          width: 32px;
          height: 32px;
          font-size: 24px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout {
          padding-right: 20px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout--with-media {
          grid-template: 'links media' / minmax(0,1fr) minmax(180px,22%);
          gap: 24px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout--with-media:has(> .lcn-menu-media[data-image-side="left"]) {
          grid-template: 'media links' / minmax(180px,22%) minmax(0,1fr);
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout--with-media:has(> .lcn-menu-media--two) {
          grid-template: 'links media' / minmax(0,1fr) minmax(280px,34%);
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout--with-media:has(> .lcn-menu-media--two[data-image-side="left"]) {
          grid-template: 'media links' / minmax(280px,34%) minmax(0,1fr);
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-media {
          gap: 14px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-image {
          height: 220px;
          aspect-ratio: auto;
          object-fit: cover;
        }

        lcn-navigation .lcn-desktop-list .lcn-columns:not(.lcn-columns--brands) {
          grid-template-columns: repeat(auto-fit,minmax(min(150px,100%),1fr));
          gap: 22px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-links--new > .lcn-columns {
          grid-template-columns: repeat(4,minmax(0,1fr));
        }

        lcn-navigation .lcn-compact-services {
          display: grid;
          grid-template-columns: repeat(4,minmax(0,1fr));
          gap: 24px;
          align-items: start;
        }

        lcn-navigation .lcn-desktop-list .lcn-column-stack {
          gap: 22px;
        }

        lcn-navigation .lcn-desktop-list .lcn-column-title {
          margin-bottom: 11px;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.45;
          letter-spacing: .02em;
          color: #706b65;
        }

        lcn-navigation .lcn-desktop-list .lcn-links a,
        lcn-navigation .lcn-desktop-list .lcn-subgroup > summary {
          min-height: 31px;
          padding-block: 5px;
          font-size: 13px;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }

        lcn-navigation .lcn-desktop-list .lcn-panel-link {
          margin-top: 18px;
          padding-top: 12px;
        }
      }

      @media (min-width: 900px) and (max-width: 1199px) {
        lcn-navigation .lcn-compact-services {
          gap: 16px;
        }

        lcn-navigation .lcn-desktop-list .lcn-menu-layout--with-media {
          gap: 18px;
        }

        lcn-navigation .lcn-desktop-list .lcn-links a {
          font-size: 12px;
        }
      }

      @media (max-width: 899px) {
        .lcn-book > span[aria-hidden="true"] {
          display: none;
        }

        .lcn-drawer[data-show-booking="false"] .lcn-book {
          display: none !important;
        }

        .lcn-drawer[data-show-booking="false"] .lcn-drawer-footer .lcn-rewards {
          margin-bottom: 0;
        }

        .lcn-drawer .lcn-drawer-footer {
          padding-top: 10px;
        }

        .lcn-drawer .lcn-menu-image {
          aspect-ratio: 4 / 3;
          max-height: 260px;
        }

        .lcn-drawer[data-mobile-detail] {
          --lcn-highlight: var(--lcn-paper);
        }

        .lcn-drawer[data-mobile-detail] .lcn-mobile-page,
        .lcn-drawer[data-mobile-detail] .lcn-brand-tools,
        .lcn-drawer[data-mobile-detail] .lcn-drawer-heading,
        .lcn-drawer[data-mobile-detail] .lcn-drawer-footer {
          background: var(--lcn-paper);
        }

        .lcn-drawer[data-mobile-detail] [data-mobile-page="skincare"] .lcn-columns,
        .lcn-drawer[data-mobile-detail] [data-mobile-page="skincare"] .lcn-column-stack {
          gap: 0;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-group {
          border-bottom: 1px solid var(--lcn-line);
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-group > summary {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          min-height: 58px;
          padding: 16px 0;
          font: 400 16px/1.5 var(--text-font-family, sans-serif);
          color: inherit;
          text-transform: none;
          letter-spacing: normal;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-group > .lcn-links {
          padding: 0 0 12px 12px;
        }

        .lcn-drawer[data-mobile-detail] [data-mobile-page="skincare"] .lcn-links a,
        .lcn-drawer[data-mobile-detail] [data-mobile-page="skincare"] .lcn-subgroup > summary {
          min-height: 54px;
          padding-block: 13px;
          font-size: 16px;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-group .lcn-links > li + li {
          border-top: 1px solid var(--lcn-line);
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-link-group .lcn-column-title {
          margin: 0;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-link-group .lcn-column-title > a {
          display: flex;
          align-items: center;
          min-height: 58px;
          padding-block: 16px;
          border-bottom: 1px solid var(--lcn-line);
          font: 400 16px/1.5 var(--text-font-family, sans-serif);
        }

        .lcn-drawer[data-mobile-detail] [data-mobile-page="brands"] .lcn-brand-directory > .lcn-column-title {
          display: none;
        }

        .lcn-drawer[data-mobile-detail] [data-mobile-page="brands"] .lcn-brand-tools {
          position: sticky;
          top: 0;
          z-index: 2;
          padding: 12px;
          margin-bottom: 10px;
          border: 1px solid var(--lcn-line);
          border-radius: 12px;
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-filter > span {
          font-size: 14px;
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-filter input {
          min-height: 50px;
          font-size: 16px;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-letter-picker {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px 14px;
          margin: 12px 0;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-letter-picker > span {
          font-size: 14px;
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-letter-picker > select {
          flex: 1;
          min-width: 120px;
          min-height: 48px;
          padding: 10px 14px;
          border: 1px solid var(--lcn-line);
          border-radius: 999px;
          background: var(--lcn-paper);
          color: inherit;
          font: 400 16px/1.5 var(--text-font-family, sans-serif);
        }

        .lcn-drawer[data-mobile-detail] .lcn-detail-letter-picker > select:focus-visible {
          outline: 2px solid currentColor;
          outline-offset: 4px;
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-list > li {
          border-bottom: 1px solid var(--lcn-line);
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-list .lcn-brand-entry > a {
          min-height: 54px;
          padding-block: 13px;
          font-size: 16px;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-entry:has(.lcn-brand-extra) > a {
          margin-right: 52px;
          padding-right: 0;
        }

        .lcn-drawer[data-mobile-detail] .lcn-brand-extra > summary {
          min-width: 48px;
          min-height: 54px;
        }
      }
    `;
    document.head.append(style);
  }

  const enhance = nav => {
    const dialog = nav.querySelector('.lcn-drawer');
    if (!dialog || dialog.dataset.mobileDetail || !nav.dataset.ready) return;

    dialog.dataset.mobileDetail = 'true';

    const fr = document.documentElement.lang.toLowerCase().startsWith('fr');
    const labels = fr ? {
      browse: 'Parcourir A–Z',
      all: 'Toutes les marques',
      view: 'Voir tout'
    } : {
      browse: 'Browse A–Z',
      all: 'All brands',
      view: 'View all'
    };

    for (const page of dialog.querySelectorAll(
      '[data-mobile-page="skincare"], [data-mobile-page="body"], [data-mobile-page="services"], [data-mobile-page="new"]'
    )) {
      page.setAttribute('data-lcn-accordion-page', '');

      for (const column of page.querySelectorAll('.lcn-column:not(.lcn-editorial)')) {
        const heading = column.querySelector(':scope > .lcn-column-title');
        const links = column.querySelector(':scope > .lcn-links');
        if (!heading) continue;

        if (!links?.children.length) {
          column.classList.add('lcn-detail-link-group');
          continue;
        }

        const group = document.createElement('details');
        group.className = 'lcn-detail-group';
        group.setAttribute('name', `${dialog.id || 'lcn-menu'}-${page.dataset.mobilePage}`);

        const summary = document.createElement('summary');
        const title = document.createElement('span');
        title.textContent = heading.textContent.trim();

        const chevron = document.createElement('span');
        chevron.className = 'lcn-chevron';
        chevron.setAttribute('aria-hidden', 'true');
        summary.append(title, chevron);

        const parent = heading.querySelector('a[href]');

        if (parent && parent.getAttribute('href') !== '#') {
          const item = document.createElement('li');
          const link = parent.cloneNode(true);
          link.className = 'lcn-view-all';
          link.textContent = `${labels.view} — ${title.textContent}`;
          item.append(link);
          links.prepend(item);
        }

        group.append(summary, links);
        heading.replaceWith(group);

        group.addEventListener('toggle', () => {
          if (!group.open) return;
          for (const other of page.querySelectorAll('.lcn-detail-group')) {
            if (other !== group) other.open = false;
          }
        });
      }
    }

    for (const directory of dialog.querySelectorAll(
      '[data-mobile-page="brands"] [data-brand-directory]'
    )) {
      const tools = directory.querySelector('.lcn-brand-tools');
      const letters = directory.querySelector('.lcn-brand-letters');
      const input = directory.querySelector('[data-brand-filter]');
      if (!tools || !letters || !input) continue;

      const buttons = [...letters.querySelectorAll('[data-brand-letter]')];
      const picker = document.createElement('label');
      picker.className = 'lcn-detail-letter-picker';

      const caption = document.createElement('span');
      caption.textContent = labels.browse;

      const select = document.createElement('select');

      for (const button of buttons) {
        const option = document.createElement('option');
        option.value = button.dataset.brandLetter;
        option.textContent = option.value === 'all' ? labels.all : option.value;
        option.disabled = button.disabled;
        select.append(option);
      }

      picker.append(caption, select);
      tools.insertBefore(picker, letters);
      letters.hidden = true;

      const update = () => {
        select.value = buttons.find(
          button => button.getAttribute('aria-pressed') === 'true'
        )?.dataset.brandLetter || 'all';
      };

      select.addEventListener('change', () => {
        const chosen = buttons.find(button => button.dataset.brandLetter === select.value);
        if (chosen && !chosen.disabled) chosen.click();
        update();
      });

      input.addEventListener('input', update);
      letters.addEventListener('click', update);
      update();
    }
  };

  const scan = root => {
    if (root.matches?.('lcn-navigation')) enhance(root);
    for (const nav of root.querySelectorAll?.('lcn-navigation') || []) enhance(nav);
  };

  if (window.customElements) {
    customElements.whenDefined('lcn-navigation').then(() => scan(document));
  }

  document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();

(() => {
  const styleId = 'lcn-services-booking-restore-style';

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @media (min-width: 900px) {
        lcn-navigation .lcn-desktop-list .lcn-panel.lcn-panel--services-complete {
          max-height: calc(100dvh - var(--lcn-panel-offset, 180px) - 12px);
        }

        lcn-navigation .lcn-panel--services-complete .lcn-panel-inner {
          max-width: 1600px;
        }

        lcn-navigation .lcn-services-layout > .lcn-services-booking {
          grid-column: 1 / -1;
          grid-row: 2;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 18px 26px;
          width: 100%;
          margin-top: 0;
          padding: 20px 0 0;
          border-top: 1px solid var(--lcn-line);
          border-radius: 0;
          background: var(--lcn-paper);
        }

        lcn-navigation .lcn-services-layout:not(.lcn-menu-layout--with-media) > .lcn-services-booking {
          margin-top: 24px;
        }

        lcn-navigation .lcn-services-booking-copy {
          flex: 1 1 300px;
          min-width: 0;
        }

        lcn-navigation .lcn-services-booking .lcn-eyebrow {
          margin: 0 0 6px;
        }

        lcn-navigation .lcn-services-booking .lcn-editorial-title {
          margin: 0 0 6px;
          font-size: 21px;
          line-height: 1.35;
        }

        lcn-navigation .lcn-services-booking p {
          margin: 0;
          font-size: 13px;
          line-height: 1.6;
        }

        lcn-navigation .lcn-services-booking-actions {
          flex: 1 1 480px;
          display: flex;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
          gap: 14px 20px;
        }

        lcn-navigation .lcn-services-booking-actions .lcn-book {
          width: auto;
          min-height: 46px;
          padding: 12px 22px;
          font-size: 13px;
          white-space: nowrap;
        }

        lcn-navigation .lcn-services-booking-actions .lcn-text-link {
          margin: 0;
          font-size: 13px;
          line-height: 1.5;
        }
      }

      @media (min-width: 900px) and (max-width: 1199px) {
        lcn-navigation .lcn-services-booking-actions {
          justify-content: flex-start;
        }
      }
    `;
    document.head.append(style);
  }

  const restore = nav => {
    if (!nav.dataset.ready) return;

    const source = nav.querySelector(
      '.lcn-desktop-list .lcn-menu-links--services > .lcn-columns'
    );

    const layout = source?.closest('.lcn-menu-layout');
    const booking = source?.querySelector('.lcn-editorial');

    if (!layout || !booking || layout.querySelector('.lcn-services-booking')) return;

    const copy = document.createElement('div');
    copy.className = 'lcn-services-booking-copy';

    const actions = document.createElement('div');
    actions.className = 'lcn-services-booking-actions';

    for (const child of [...booking.children]) {
      if (child.matches('a')) actions.append(child);
      else copy.append(child);
    }

    booking.append(copy, actions);
    booking.classList.add('lcn-services-booking');
    booking.hidden = false;

    layout.classList.add('lcn-services-layout');
    layout.closest('.lcn-panel')?.classList.add('lcn-panel--services-complete');
    layout.append(booking);
  };

  const scan = root => {
    if (root.matches?.('lcn-navigation')) restore(root);
    for (const nav of root.querySelectorAll?.('lcn-navigation') || []) restore(nav);
  };

  customElements.whenDefined('lcn-navigation').then(() => scan(document));
  document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();

(() => {
  const styleId = 'lcn-menu-refinement';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    lcn-navigation[data-lcn-refined],
    .lcn-drawer[data-lcn-refined] {
      --lcn-line: #ded8d1;
      --lcn-soft: #f4f1ee;
    }

    lcn-navigation[data-lcn-refined] .lcn-brand-tools,
    .lcn-drawer[data-lcn-refined][data-mobile-detail] [data-mobile-page="brands"] .lcn-brand-tools {
      background: var(--lcn-soft);
      border: 1px solid var(--lcn-line);
      border-radius: 12px;
      padding: 16px;
      margin-bottom: 16px;
    }

    lcn-navigation[data-lcn-refined] .lcn-brand-filter > span {
      font-weight: 600;
    }

    lcn-navigation[data-lcn-refined] .lcn-brand-filter input,
    lcn-navigation[data-lcn-refined] .lcn-brand-letter,
    .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-letter-picker > select,
    .lcn-drawer[data-lcn-refined] .lcn-localization select {
      background: #fff;
      border-color: var(--lcn-line);
    }

    lcn-navigation[data-lcn-refined] .lcn-brand-letter[aria-pressed="true"] {
      background: #e2dbd3;
      border-color: #8b8176;
    }

    .lcn-drawer[data-lcn-refined] .lcn-drawer-brand {
      display: flex;
      align-items: center;
      min-height: 44px;
    }

    .lcn-drawer[data-lcn-refined] .lcn-menu-logo {
      display: block;
      width: 180px;
      max-width: 100%;
      height: auto;
      opacity: 1;
      position: static;
      transform: none;
    }

    .lcn-drawer[data-lcn-refined] .lcn-mobile-choice {
      background: var(--lcn-soft);
      border-color: var(--lcn-line);
      border-radius: 999px;
      min-height: 56px;
      padding: 12px 16px;
      gap: 8px;
      font-size: 14px;
      line-height: 1.4;
    }

    .lcn-drawer[data-lcn-refined] .lcn-localization {
      background: var(--lcn-soft);
      border: 1px solid var(--lcn-line);
      border-radius: 24px;
      padding: 14px;
      gap: 10px;
    }

    .lcn-drawer[data-lcn-refined] .lcn-localization select {
      min-width: 0;
      padding-inline: 12px;
      border-radius: 999px;
      min-height: 48px;
    }

    .lcn-drawer[data-lcn-refined] .lcn-close:focus:not(:focus-visible) {
      outline: none;
    }

    @media (max-width: 899px) {
      .lcn-drawer[data-lcn-refined] {
        --heading-font-family: Muli, sans-serif;
        --text-font-family: Muli, sans-serif;
        font-family: Muli, sans-serif;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] :is(a, button, input, select, option, textarea, summary, h1, h2, h3, h4, h5, h6, p, span, label) {
        font-family: Muli, sans-serif;
      }

      .lcn-drawer[data-lcn-refined] .lcn-mobile-page-heading > h2,
      .lcn-drawer[data-lcn-refined] .lcn-editorial-title {
        font-size: 21px;
        line-height: 1.4;
        font-weight: 400;
        letter-spacing: normal;
        text-transform: none;
      }

      .lcn-drawer[data-lcn-refined] .lcn-close {
        flex-shrink: 0;
        border-radius: 50%;
      }

      .lcn-drawer[data-lcn-refined] .lcn-mobile-page-heading > button {
        border: 1px solid var(--lcn-line);
        border-radius: 999px;
        padding: 10px 16px;
        min-height: 44px;
        font-size: 13px;
      }

      .lcn-drawer[data-lcn-refined] .lcn-mobile-choice > span:first-child {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] [data-lcn-accordion-page] .lcn-columns,
      .lcn-drawer[data-lcn-refined][data-mobile-detail] [data-lcn-accordion-page] .lcn-column-stack {
        gap: 10px;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group {
        border: 0;
        border-radius: 26px;
        margin: 0;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group > summary {
        min-height: 54px;
        padding: 14px 18px;
        border: 1px solid var(--lcn-line);
        border-radius: 999px;
        background: var(--lcn-soft);
        font-size: 15px;
        line-height: 1.5;
        cursor: pointer;
        list-style: none;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group[open] {
        background: var(--lcn-soft);
        box-shadow: inset 0 0 0 1px var(--lcn-line);
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group[open] > summary {
        font-weight: 700;
        border-color: transparent;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group > .lcn-links {
        padding: 0 18px 12px;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group .lcn-links a,
      .lcn-drawer[data-lcn-refined][data-mobile-detail] .lcn-detail-group .lcn-subgroup > summary {
        min-height: 48px;
        padding-block: 12px;
        font-size: 15px;
        line-height: 1.5;
        overflow-wrap: anywhere;
      }

      .lcn-drawer[data-lcn-refined] .lcn-detail-group .lcn-view-all {
        text-decoration: underline;
        text-underline-offset: 4px;
      }

      .lcn-drawer[data-lcn-refined][data-mobile-detail] [data-mobile-page="brands"] .lcn-brand-tools,
      .lcn-drawer[data-lcn-refined] .lcn-editorial {
        border-radius: 24px;
      }

      .lcn-drawer[data-lcn-refined] .lcn-image-card {
        border-radius: 18px;
      }
    }

    @media (min-width: 900px) {
      lcn-navigation[data-lcn-refined] .lcn-compact-services .lcn-column-title {
        background: var(--lcn-soft);
        color: var(--lcn-ink);
        padding: 10px 12px;
        border-radius: 6px;
        font-weight: 600;
      }
    }
  `;
  document.head.append(style);

  const refine = nav => {
    const drawer = nav.querySelector('.lcn-drawer');
    if (!nav.dataset.ready || !drawer || nav.hasAttribute('data-lcn-refined')) return;

    nav.setAttribute('data-lcn-refined', '');
    drawer.setAttribute('data-lcn-refined', '');

    const bar = document.querySelector('.lc-announcement-section');
    const colour = bar && getComputedStyle(bar).getPropertyValue('--lc-bar-background').trim();

    if (colour) {
      nav.style.setProperty('--lcn-soft', colour);
      drawer.style.setProperty('--lcn-soft', colour);
    }

    const logo = nav.closest('.lcn-header')?.querySelector(
      '.header__logo-image:not(.header__logo-image--transparent)'
    );
    const brand = drawer.querySelector('.lcn-drawer-brand');

    if (logo && brand) {
      const image = logo.cloneNode(true);
      image.className = 'lcn-menu-logo';
      image.removeAttribute('id');
      image.removeAttribute('style');
      image.removeAttribute('itemprop');
      image.alt = brand.textContent.trim();
      image.loading = 'eager';
      image.sizes = '180px';
      brand.replaceChildren(image);
    }
  };

  const scan = root => {
    if (root.matches?.('lcn-navigation')) refine(root);
    root.querySelectorAll?.('lcn-navigation').forEach(refine);
  };

  customElements.whenDefined('lcn-navigation').then(() => scan(document));
  document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();